import React, { useEffect, useState, createContext, useContext } from 'react';
// Define the theme type
export type Theme = 'light' | 'dark';
// Define the context type
interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean; // Convenience property
}
// Create the context with a default undefined value
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
// Props for the ThemeProvider component
interface ThemeProviderProps {
  children: React.ReactNode;
}
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children
}) => {
  // Initialize theme state from localStorage with 'light' as default
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      // Return saved theme if valid, otherwise default to light
      return savedTheme === 'dark' || savedTheme === 'light' ? savedTheme as Theme : 'light';
    } catch (error) {
      // In case of any localStorage errors, default to light
      console.error('Error reading theme from localStorage:', error);
      return 'light';
    }
  });
  // Function to update theme
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    // Persist to localStorage
    try {
      localStorage.setItem('theme', newTheme);
    } catch (error) {
      console.error('Error saving theme to localStorage:', error);
    }
  };
  // Apply theme to document whenever it changes
  useEffect(() => {
    // Set data-theme attribute for CSS variables
    document.documentElement.dataset.theme = theme;
    // Toggle dark class for compatibility with existing styles
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);
  // Create the context value
  const contextValue: ThemeContextType = {
    theme,
    setTheme,
    isDark: theme === 'dark'
  };
  return <ThemeContext.Provider value={contextValue} data-id="element-11012">
      {children}
    </ThemeContext.Provider>;
};
// Custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};